import { Box, Button, Center, Group, Loader, Stack, Tooltip } from '@mantine/core';
import { IconArrowBackUp } from '@tabler/icons-react';
import { iconProps } from 'components/icon-props';
import { useProfile } from 'init/use-enforce-profile';
import { i18nKeys, useTranslation } from 'locales';
import { useLoadMail } from 'modules/Mails/hooks';
import { getLastMail } from 'modules/Mails/utils/thread';
import { useParams } from 'react-router';
import { isNullish } from 'remeda';
import { MailDetailComposer } from './components/MailDetailComposer';
import { MailDetailHeader } from './components/MailDetailHeader';
import { MailDetailKeyboardHints } from './components/MailDetailKeyboardHints';
import { MailDetailThread } from './components/MailDetailThread';
import { useMailDetailActions } from './hooks/use-mail-detail-actions';
import { useMailDetailNavigation } from './hooks/use-mail-detail-navigation';
import { useMailDetailReply } from './hooks/use-mail-detail-reply';
import { useMailDetailShortcuts } from './hooks/use-mail-detail-shortcuts';

export const MailDetail = () => {
  const { t } = useTranslation();
  const profile = useProfile();
  const { id } = useParams<{ id: string }>();

  const { thread, refetchThread, isThreadInitialLoading } = useLoadMail(id);
  const { prevId, nextId, goToPrev, goToNext } = useMailDetailNavigation(id);
  const { handleToggleStatus, handleAssign } = useMailDetailActions({
    id,
    thread,
    refetchThread,
  });
  const {
    isReplyEnabled,
    replyDisabledReason,
    isReplying,
    openReply,
    closeReply,
    focusAiPromptSignal,
    draftMail,
    userSignature,
  } = useMailDetailReply();

  const lastMail = getLastMail(thread);

  useMailDetailShortcuts({
    isReady: !isThreadInitialLoading && !isNullish(lastMail),
    canReply: isReplyEnabled,
    canAssign: profile.isAdmin,
    canGoPrev: prevId !== undefined,
    canGoNext: nextId !== undefined,
    openReply: () => openReply({ trigger: 'shortcut' }),
    toggleStatus: handleToggleStatus,
    openAssign: handleAssign,
    goPrev: goToPrev,
    goNext: goToNext,
  });

  if (isThreadInitialLoading)
    return (
      <Center h="100%">
        <Loader />
      </Center>
    );

  if (isNullish(lastMail)) return null;

  return (
    <Stack gap={0} h="100%" style={{ overflow: 'hidden' }}>
      <MailDetailHeader openReply={openReply} />
      <Box style={{ flex: 1, overflowY: 'auto' }}>
        <MailDetailThread key={id} />
        {isReplying ? (
          <MailDetailComposer
            closeReply={closeReply}
            draftMail={draftMail}
            userSignature={userSignature}
            focusAiPromptSignal={focusAiPromptSignal}
          />
        ) : (
          <Group px="space8" py="space6" gap="space2" justify="space-between">
            <Tooltip label={replyDisabledReason} disabled={isReplyEnabled}>
              <Button
                onClick={() => openReply()}
                disabled={!isReplyEnabled}
                leftSection={<IconArrowBackUp {...iconProps(16)} />}
              >
                {t(i18nKeys.REPLY)}
              </Button>
            </Tooltip>
            <MailDetailKeyboardHints />
          </Group>
        )}
      </Box>
    </Stack>
  );
};
